import React, { useState, useEffect, useRef, useCallback } from 'react';
import * as pdfjsLib from 'pdfjs-dist';
import { FaArrowLeft, FaChevronRight, FaCopy, FaCheck, FaMapMarkerAlt, FaSave, FaExternalLinkAlt, FaTimes, FaUpload } from 'react-icons/fa';
import { getApiEndpoint } from '../../../config/api';
import { toast } from 'react-toastify';
import './AssetTagger.css';

pdfjsLib.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.js';

const VARIANTS = {
  stainless_steel: { label: 'Stainless Steel (SS)', code: 'SS' },
  galvanised:      { label: 'Galvanised (G)',        code: 'G'  },
  aluminium:       { label: 'Aluminium (AL)',         code: 'AL' },
  steel:           { label: 'Steel (ST)',             code: 'ST' },
  concrete:        { label: 'Concrete (C)',           code: 'C'  },
  temporary:       { label: 'Temporary (T)',          code: 'T'  },
  permanent:       { label: 'Permanent (P)',          code: 'P'  },
};
const FIXINGS = {
  chemfix:               { label: 'Chemfix (CF)',               code: 'CF' },
  through_bolt:          { label: 'Through Bolt (TB)',          code: 'TB' },
  cast_in:               { label: 'Cast In (CI)',               code: 'CI' },
  welded:                { label: 'Welded (WL)',                code: 'WL' },
  clamped:               { label: 'Clamped (CL)',               code: 'CL' },
  bolted_to_steel:       { label: 'Bolted to Steel (BS)',       code: 'BS' },
  surface_fixed:         { label: 'Surface Fixed (SF)',         code: 'SF' },
  ballasted:             { label: 'Ballasted (BA)',             code: 'BA' },
  manufacturer_specific: { label: 'Manufacturer Specific (MS)', code: 'MS' },
};
const t = (value, label, code, variants, fixings) => ({ value, label, code, variants, fixings });
const CATALOG = {
  anchor_points: { label: 'Anchor Points', code: 'AP', subtypes: [
    t('fixed_anchor','Fixed Anchor','FX',['stainless_steel','galvanised','aluminium','steel'],['chemfix','through_bolt','cast_in','welded','bolted_to_steel','surface_fixed']),
    t('eyebolt','Eyebolt','EB',['stainless_steel','galvanised','steel'],['chemfix','through_bolt','cast_in','welded','bolted_to_steel','surface_fixed']),
    t('chemical_anchor','Chemical Anchor','CH',['concrete','steel'],['chemfix','through_bolt','surface_fixed']),
    t('mechanical_anchor','Mechanical Anchor','ME',['steel','stainless_steel'],['through_bolt','bolted_to_steel','surface_fixed']),
    t('concrete_anchor','Concrete Anchor','CO',['concrete'],['cast_in','chemfix','through_bolt']),
    t('steel_anchor','Steel Anchor','ST',['steel','galvanised'],['welded','bolted_to_steel','clamped','surface_fixed']),
    t('timber_anchor','Timber Anchor','TI',['temporary','permanent'],['through_bolt','surface_fixed','manufacturer_specific']),
    t('mobile_anchor','Mobile Anchor','MO',['temporary'],['ballasted','clamped','manufacturer_specific']),
    t('temporary_anchor','Temporary Anchor','TA',['temporary'],['ballasted','clamped','surface_fixed','manufacturer_specific']),
  ]},
  davit_sockets: { label: 'Davit Sockets', code: 'DS', subtypes: [
    t('cast_in_socket','Cast-in Socket','CI',['stainless_steel','galvanised'],['cast_in']),
    t('wall_mounted_socket','Wall Mounted Socket','WM',['stainless_steel','galvanised','aluminium'],['chemfix','through_bolt','welded','bolted_to_steel','surface_fixed']),
    t('surface_mounted_socket','Surface Mounted Socket','SM',['stainless_steel','galvanised'],['chemfix','through_bolt','welded','bolted_to_steel','surface_fixed']),
    t('removable_socket','Removable Socket','RS',['stainless_steel','galvanised'],['clamped','bolted_to_steel','surface_fixed','manufacturer_specific']),
    t('temporary_socket','Temporary Socket','TS',['temporary'],['ballasted','clamped','surface_fixed']),
    t('recessed_socket','Recessed Socket','RC',['stainless_steel','galvanised'],['cast_in','chemfix','through_bolt']),
    t('parapet_mounted_socket','Parapet Mounted Socket','PM',['stainless_steel','galvanised'],['chemfix','through_bolt','welded','bolted_to_steel','surface_fixed']),
  ]},
  lifelines: { label: 'Lifelines', code: 'LL', subtypes: [
    t('horizontal_lifeline','Horizontal Lifeline','HL',['galvanised','stainless_steel','steel'],['chemfix','through_bolt','welded','bolted_to_steel','surface_fixed']),
    t('vertical_lifeline','Vertical Lifeline','VL',['galvanised','stainless_steel'],['chemfix','through_bolt','welded','bolted_to_steel','surface_fixed']),
    t('rigid_lifeline','Rigid Lifeline','RL',['steel','aluminium'],['welded','bolted_to_steel','clamped','surface_fixed']),
    t('flexible_lifeline','Flexible Lifeline','FL',['temporary','permanent'],['clamped','surface_fixed','manufacturer_specific']),
    t('cable_lifeline','Cable Lifeline','CL',['galvanised','stainless_steel'],['clamped','surface_fixed','manufacturer_specific']),
    t('rail_system','Rail System','RS',['steel','aluminium'],['welded','bolted_to_steel','surface_fixed']),
  ]},
  abseil_systems: { label: 'Abseil Systems', code: 'AS', subtypes: [
    t('abseil_anchor','Abseil Anchor','AA',['stainless_steel','steel'],['chemfix','through_bolt','welded','bolted_to_steel','surface_fixed']),
    t('rope_access_anchor','Rope Access Anchor','RA',['stainless_steel','steel'],['chemfix','through_bolt','welded','bolted_to_steel','surface_fixed']),
    t('abseil_rail','Abseil Rail','AR',['stainless_steel','aluminium'],['welded','bolted_to_steel','surface_fixed']),
    t('rope_access_line','Rope Access Line','RL',['temporary','permanent'],['clamped','surface_fixed','manufacturer_specific']),
    t('abseil_davit','Abseil Davit','AD',['stainless_steel','galvanised'],['chemfix','through_bolt','welded','bolted_to_steel','surface_fixed']),
  ]},
  cradles_bmus: { label: 'Cradles / BMUs', code: 'CR', subtypes: [
    t('building_maintenance_unit','Building Maintenance Unit','BM',['permanent'],['welded','bolted_to_steel','surface_fixed','manufacturer_specific']),
    t('fixed_cradle','Fixed Cradle','FC',['steel','aluminium'],['welded','bolted_to_steel','surface_fixed']),
    t('monorail_cradle','Monorail Cradle','MR',['steel','aluminium'],['welded','bolted_to_steel','surface_fixed']),
    t('track_system','Track System','TS',['steel','aluminium'],['welded','bolted_to_steel','surface_fixed']),
    t('davit_cradle','Davit Cradle','DC',['steel','aluminium'],['welded','bolted_to_steel','surface_fixed']),
    t('suspended_platform','Suspended Platform','SP',['temporary','permanent'],['clamped','surface_fixed','manufacturer_specific']),
  ]},
  edge_protection: { label: 'Edge Protection', code: 'EP', subtypes: [
    t('temporary_edge_protection','Temporary Edge Protection','TE',['temporary'],['ballasted','clamped','surface_fixed']),
    t('permanent_edge_protection','Permanent Edge Protection','PE',['permanent','stainless_steel','galvanised'],['chemfix','through_bolt','welded','bolted_to_steel','surface_fixed']),
    t('parapet_guard','Parapet Guard','PG',['stainless_steel','galvanised','aluminium'],['chemfix','through_bolt','welded','bolted_to_steel','surface_fixed']),
    t('handrail_system','Handrail System','HR',['stainless_steel','galvanised','aluminium'],['welded','bolted_to_steel','surface_fixed']),
    t('ballasted_system','Ballasted System','BS',['temporary'],['ballasted']),
  ]},
  walkways_access: { label: 'Walkways / Access', code: 'WA', subtypes: [
    t('roof_walkway','Roof Walkway','RW',['steel','aluminium','galvanised'],['welded','bolted_to_steel','surface_fixed']),
    t('step_over','Step-over','SO',['steel','aluminium'],['welded','bolted_to_steel','surface_fixed']),
    t('access_platform','Access Platform','AP',['steel','aluminium'],['welded','bolted_to_steel','surface_fixed']),
    t('roof_hatch','Roof Hatch','RH',['steel','aluminium'],['welded','bolted_to_steel','surface_fixed']),
    t('cat_ladder','Cat Ladder','CL',['steel','aluminium','galvanised'],['welded','bolted_to_steel','surface_fixed']),
  ]},
  ladders: { label: 'Ladders', code: 'LD', subtypes: [
    t('fixed_ladder','Fixed Ladder','FL',['steel','aluminium','galvanised'],['welded','bolted_to_steel','surface_fixed']),
    t('vertical_ladder','Vertical Ladder','VL',['steel','aluminium','galvanised'],['welded','bolted_to_steel','surface_fixed']),
    t('inclined_ladder','Inclined Ladder','IL',['steel','aluminium'],['welded','bolted_to_steel','surface_fixed']),
    t('caged_ladder','Caged Ladder','CL',['steel','galvanised'],['welded','bolted_to_steel','surface_fixed']),
    t('roof_ladder','Roof Ladder','RL',['steel','aluminium'],['welded','bolted_to_steel','surface_fixed']),
  ]},
  guardrails_handrails: { label: 'Guardrails / Handrails', code: 'GR', subtypes: [
    t('guardrail','Guardrail','GR',['stainless_steel','galvanised','aluminium','steel'],['welded','bolted_to_steel','surface_fixed']),
    t('handrail','Handrail','HR',['stainless_steel','galvanised','aluminium'],['welded','bolted_to_steel','surface_fixed']),
    t('balustrade','Balustrade','BL',['stainless_steel','aluminium'],['welded','bolted_to_steel','surface_fixed']),
    t('knee_rail','Knee Rail','KR',['steel','galvanised'],['welded','bolted_to_steel','surface_fixed']),
  ]},
  roof_anchors_non_fall_arrest: { label: 'Roof Anchors (Non-fall arrest)', code: 'RA', subtypes: [
    t('non_fall_arrest_anchor','Non-fall Arrest Anchor','NF',['stainless_steel','steel'],['chemfix','through_bolt','welded','bolted_to_steel','surface_fixed']),
    t('tie_off_point','Tie-off Point','TP',['stainless_steel','steel'],['chemfix','through_bolt','welded','bolted_to_steel','surface_fixed']),
  ]},
  rescue_equipment: { label: 'Rescue Equipment', code: 'RE', subtypes: [
    t('rescue_winch','Rescue Winch','RW',['permanent'],['welded','bolted_to_steel','surface_fixed','manufacturer_specific']),
    t('rescue_kit','Rescue Kit','RK',['temporary','permanent'],['clamped','surface_fixed','manufacturer_specific']),
    t('retrieval_device','Retrieval Device','RD',['temporary','permanent'],['clamped','surface_fixed','manufacturer_specific']),
    t('rescue_anchor','Rescue Anchor','RA',['stainless_steel','steel'],['chemfix','through_bolt','welded','bolted_to_steel','surface_fixed']),
  ]},
  temporary_works: { label: 'Temporary Works', code: 'TW', subtypes: [
    t('temporary_anchor','Temporary Anchor','TA',['temporary'],['ballasted','clamped','surface_fixed']),
    t('temporary_guardrail','Temporary Guardrail','TG',['temporary'],['ballasted','clamped','surface_fixed']),
    t('temporary_platform','Temporary Platform','TP',['temporary'],['ballasted','clamped','surface_fixed']),
  ]},
};
const CATALOG_KEYS = Object.keys(CATALOG);

// ─── Step constants ───────────────────────────────────────
const STEP_PROPERTY    = 'property';
const STEP_FLOOR_PLAN  = 'floor_plan';
const STEP_MAP         = 'map';

export default function AssetTagger() {
  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };
  // ── Navigation state ──
  const [step, setStep]                       = useState(STEP_PROPERTY);
  const [properties, setProperties]           = useState([]);
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [floorPlans, setFloorPlans]           = useState([]);
  const [selectedPlan, setSelectedPlan]       = useState(null);
  const [loadingProps, setLoadingProps]       = useState(true);
  const [loadingPlans, setLoadingPlans]       = useState(false);

  // ── Canvas / PDF state ──
  const canvasRef   = useRef(null);
  const wrapRef     = useRef(null);
  const [pdfLoading, setPdfLoading]           = useState(false);
  const [canvasSize, setCanvasSize]           = useState({ w: 0, h: 0 });

  // ── Pin / sheet state ──
  const [pins, setPins]                       = useState([]); // { x_pct, y_pct, num, saved, assetCode }
  const [pendingPin, setPendingPin]           = useState(null); // { x_pct, y_pct }
  const [sheetOpen, setSheetOpen]             = useState(false);
  const [catKey, setCatKey]                   = useState(CATALOG_KEYS[0]);
  const [subtypeVal, setSubtypeVal]           = useState(CATALOG[CATALOG_KEYS[0]]?.subtypes?.[0]?.value || '');
  const [variantVal, setVariantVal]           = useState(CATALOG[CATALOG_KEYS[0]]?.subtypes?.[0]?.variants?.[0] || '');
  const [fixingVal, setFixingVal]             = useState(CATALOG[CATALOG_KEYS[0]]?.subtypes?.[0]?.fixings?.[0] || '');
  const [locationDesc, setLocationDesc]       = useState('');
  const [notes, setNotes]                     = useState('');
  const [saving, setSaving]                   = useState(false);
  const [photoFile, setPhotoFile]             = useState(null);
  const [photoPreview, setPhotoPreview]       = useState(null);
  const photoInputRef                         = useRef(null);
  const [sessionAssets, setSessionAssets]     = useState([]); // { assetCode, typeLabel }
  const [copiedCode, setCopiedCode]           = useState(null);
  const [successFlash, setSuccessFlash]       = useState(false);
  const [selectedPin, setSelectedPin]         = useState(null); // existing pin popup
  const [savedAsset, setSavedAsset]           = useState(null); // { code, id } — post-save confirmation

  // ── Zoom / pan state ──
  const [zoom, setZoom]                       = useState(1);
  const [pan, setPan]                         = useState({ x: 0, y: 0 });
  const lastTouchRef                          = useRef(null); // for pinch tracking
  const isPinchingRef                         = useRef(false);
  const outerRef                              = useRef(null); // for non-passive touch listener

  // Block browser native pinch-zoom on this element so our in-canvas zoom works exclusively
  useEffect(() => {
    const el = outerRef.current;
    if (!el) return;
    const prevent = (e) => { if (e.touches && e.touches.length > 1) e.preventDefault(); };
    el.addEventListener('touchstart', prevent, { passive: false });
    el.addEventListener('touchmove',  prevent, { passive: false });
    return () => {
      el.removeEventListener('touchstart', prevent);
      el.removeEventListener('touchmove',  prevent);
    };
  }, []);

  // ── Upload state ──
  const uploadInputRef = useRef(null);
  const [uploading, setUploading]             = useState(false);
  const [pendingFile, setPendingFile]         = useState(null);
  const [uploadName, setUploadName]           = useState('');
  const [uploadFloor, setUploadFloor]         = useState('');
  const [uploadFloorCustom, setUploadFloorCustom] = useState('');
  const [propertyFloors, setPropertyFloors]   = useState([]);
  const [uploadSection, setUploadSection]     = useState('');
  const [uploadModalStep, setUploadModalStep] = useState(1); // 1=name, 2=floor, 3=section
  const [showNameModal, setShowNameModal]     = useState(false);

  // ── Derived ──
  const catDef = CATALOG[catKey];
  const currentSubtype = catDef?.subtypes?.find(s => s.value === subtypeVal) || catDef?.subtypes?.[0] || null;
  const variantOptions = (currentSubtype?.variants || []).map(v => ({ value: v, ...VARIANTS[v] }));
  const fixingOptions  = (currentSubtype?.fixings  || []).map(f => ({ value: f, ...FIXINGS[f]  }));
  const previewCode = selectedProperty
    ? `${selectedProperty.property_code || 'XXX'}-${catDef?.code || 'XX'}-${String((sessionAssets.length + 1)).padStart(3, '0')}`
    : '---';

  // ─────────────────────────────────────────────────────────
  // Load properties
  // ─────────────────────────────────────────────────────────
  useEffect(() => {
    async function load() {
      setLoadingProps(true);
      try {
        const res = await fetch(getApiEndpoint('/api/contractor/assigned-properties'), { headers });
        if (res.ok) {
          const data = await res.json();
          setProperties(data);
        }
      } catch (e) { /* silent */ }
      finally { setLoadingProps(false); }
    }
    load();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ─────────────────────────────────────────────────────────
  // Load floor plans for selected property
  // ─────────────────────────────────────────────────────────
  const loadFloorPlans = useCallback(async (propertyId) => {
    setLoadingPlans(true);
    // Also fetch property floors
    fetch(getApiEndpoint(`/api/properties/${propertyId}/tours/floors`), { headers })
      .then(r => r.ok ? r.json() : [])
      .then(data => setPropertyFloors(Array.isArray(data) ? data : []))
      .catch(() => setPropertyFloors([]));
    try {
      const res = await fetch(getApiEndpoint(`/api/properties/${propertyId}/pinpoint`), { headers });
      if (res.ok) {
        const data = await res.json();
        setFloorPlans(Array.isArray(data) ? data : (data.pinpointDocuments || []));
      } else {
        setFloorPlans([]);
      }
    } catch (e) { setFloorPlans([]); }
    finally { setLoadingPlans(false); }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // ─────────────────────────────────────────────────────────
  // Render PDF to canvas
  // ─────────────────────────────────────────────────────────
  const renderPdf = useCallback(async (filePath) => {
    setPdfLoading(true);
    setZoom(1);
    setPan({ x: 0, y: 0 });
    try {
      const clean = filePath.replace(/^uploads\//, '').replace(/^\//, '');
      const url = getApiEndpoint(`/api/uploads/${clean}`);
      const dpr = window.devicePixelRatio || 2;
      const pdf = await pdfjsLib.getDocument({ url, withCredentials: false, isEvalSupported: false }).promise;
      const page = await pdf.getPage(1);
      const wrapWidth = wrapRef.current ? wrapRef.current.offsetWidth : 360;
      const viewport = page.getViewport({ scale: 1 });
      const scale = (wrapWidth / viewport.width) * dpr;
      const scaledViewport = page.getViewport({ scale });
      const canvas = canvasRef.current;
      canvas.width  = scaledViewport.width;
      canvas.height = scaledViewport.height;
      canvas.style.width  = `${scaledViewport.width / dpr}px`;
      canvas.style.height = `${scaledViewport.height / dpr}px`;
      setCanvasSize({ w: scaledViewport.width / dpr, h: scaledViewport.height / dpr });
      await page.render({ canvasContext: canvas.getContext('2d'), viewport: scaledViewport }).promise;
    } catch (e) {
      console.error('PDF load error:', e);
      toast.error('Could not load floor plan PDF');
    }
    finally { setPdfLoading(false); }
  }, []);

  // ─────────────────────────────────────────────────────────
  // Handle tap on canvas → drop pending pin
  // ─────────────────────────────────────────────────────────
  const handleCanvasTap = useCallback((e) => {
    if (sheetOpen) return;
    if (isPinchingRef.current) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    // Get the canvas's actual rendered rect (after CSS scaling, before zoom transform)
    // We need position relative to the canvas element itself
    const outer = outerRef.current;
    if (!outer) return;
    const outerRect = outer.getBoundingClientRect();
    const touch = e.changedTouches?.[0] || e.touches?.[0] || e;
    const clientX = touch.clientX;
    const clientY = touch.clientY;
    if (clientX == null) return;
    // The wrap is scaled via transform: scale(zoom) translate(pan/zoom).
    // To get position as % of the original image, we undo the transform:
    const logicalX = (clientX - outerRect.left - pan.x) / zoom;
    const logicalY = (clientY - outerRect.top  - pan.y) / zoom;
    // canvas pixel dimensions give the true aspect ratio
    const naturalW = outerRect.width;
    const aspectRatio = canvas.height / canvas.width; // pixel dimensions
    const naturalH = naturalW * aspectRatio;
    const x_pct = Math.min(100, Math.max(0, (logicalX / naturalW) * 100));
    const y_pct = Math.min(100, Math.max(0, (logicalY / naturalH) * 100));
    const firstKey = CATALOG_KEYS[0];
    const firstSub = CATALOG[firstKey]?.subtypes?.[0];
    setPendingPin({ x_pct, y_pct });
    setSheetOpen(true);
    setLocationDesc('');
    setNotes('');
    setCatKey(firstKey);
    setSubtypeVal(firstSub?.value || '');
    setVariantVal(firstSub?.variants?.[0] || '');
    setFixingVal(firstSub?.fixings?.[0] || '');
    setPhotoFile(null);
    setPhotoPreview(null);
    setSavedAsset(null);
  }, [sheetOpen]);

  // ─────────────────────────────────────────────────────────
  // Save asset
  // ─────────────────────────────────────────────────────────
  const handleSave = async () => {
    if (!pendingPin || !selectedProperty || !selectedPlan) return;
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append('property_id',          selectedProperty.id);
      fd.append('pinpoint_document_id', selectedPlan.id);
      fd.append('x_percent',            pendingPin.x_pct);
      fd.append('y_percent',            pendingPin.y_pct);
      fd.append('asset_type',           catKey);
      fd.append('subtype',              subtypeVal || (catDef?.subtypes?.[0]?.value || ''));
      fd.append('variant',              variantVal);
      fd.append('fixing',               fixingVal);
      fd.append('subtype_code',         currentSubtype?.code || '');
      fd.append('variant_code',         VARIANTS[variantVal]?.code || '');
      fd.append('fixing_code',          FIXINGS[fixingVal]?.code || '');
      fd.append('location_description', locationDesc);
      fd.append('notes',                notes);
      if (photoFile) fd.append('photo', photoFile);

      const res = await fetch(getApiEndpoint('/api/contractor/tag-asset'), {
        method: 'POST',
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        body: fd,
      });
      const data = await res.json();
      if (!res.ok) {
        toast.error(data.message || 'Failed to save asset');
        return;
      }
      const { asset_code, asset_id } = data;
      // Add saved pin to map
      setPins(prev => [...prev, {
        x_pct: pendingPin.x_pct,
        y_pct: pendingPin.y_pct,
        num: prev.length + 1,
        saved: true,
        assetCode: asset_code,
        asset_id,
      }]);
      // Add to session list
      setSessionAssets(prev => [...prev, {
        assetCode: asset_code,
        typeLabel: catDef.label,
      }]);
      // Show post-save confirmation inside the sheet
      setSavedAsset({ code: asset_code, id: asset_id, catLabel: catDef.label });
    } catch (e) {
      toast.error('Failed to save asset');
    } finally {
      setSaving(false);
    }
  };

  // ─────────────────────────────────────────────────────────
  // Copy helper
  // ─────────────────────────────────────────────────────────
  const copyCode = (code) => {
    navigator.clipboard.writeText(code).catch(() => {});
    setCopiedCode(code);
    setTimeout(() => setCopiedCode(null), 2000);
  };

  // ─────────────────────────────────────────────────────────
  // Render: Step 1 — pick property
  // ─────────────────────────────────────────────────────────
  if (step === STEP_PROPERTY) {
    return (
      <div className="at-root">
        <div className="at-step-header">
          <h2>Asset Tagger — Select Property</h2>
        </div>
        <div className="at-body">
          {loadingProps ? (
            <div className="at-empty"><p>Loading properties…</p></div>
          ) : properties.length === 0 ? (
            <div className="at-empty">
              <FaMapMarkerAlt />
              <p>No properties assigned. You must be assigned to a job at a property first.</p>
            </div>
          ) : (
            <div className="at-list">
              {properties.map(p => (
                <button
                  key={p.id}
                  className="at-list-item"
                  onClick={() => {
                    setSelectedProperty(p);
                    loadFloorPlans(p.id);
                    setStep(STEP_FLOOR_PLAN);
                    setPins([]);
                    setSessionAssets([]);
                  }}
                >
                  <div>
                    <span>{p.name}</span>
                    {p.address && <span className="at-list-item-sub">{p.address}</span>}
                  </div>
                  <FaChevronRight className="at-chevron" />
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }

  // ── Upload floor plan: step 1 — pick file, show name modal ──
  const handleFileSelected = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setPendingFile(file);
    setUploadName(file.name.replace(/\.[^.]+$/, ''));
    setUploadFloor('');
    setUploadFloorCustom('');
    setUploadSection('');
    setUploadModalStep(1);
    setShowNameModal(true);
    e.target.value = '';
  };

  // ── Upload floor plan: step 2 — upload doc then convert to pinpoint ──
  const handleConfirmUpload = async () => {
    if (!pendingFile || !selectedProperty) return;
    setUploading(true);
    setShowNameModal(false);
    try {
      // 1. Upload the document
      const resolvedFloor = uploadFloor === '__custom__' ? uploadFloorCustom : uploadFloor;
      const fullName = [uploadName, resolvedFloor, uploadSection].filter(Boolean).join(' — ');
      const fd = new FormData();
      fd.append('file', pendingFile);
      fd.append('name', fullName || pendingFile.name);
      fd.append('type', 'Floor Plan');
      fd.append('docType', 'interactive');
      if (uploadSection) fd.append('zone', uploadSection);
      const uploadRes = await fetch(
        getApiEndpoint(`/api/properties/${selectedProperty.id}/documents`),
        { method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: fd }
      );
      if (!uploadRes.ok) throw new Error('Upload failed');
      const { document: doc } = await uploadRes.json();

      // 2. Convert to pinpoint document
      const pinRes = await fetch(
        getApiEndpoint(`/api/properties/${selectedProperty.id}/pinpoint`),
        {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ documentId: doc.id, name: fullName || doc.name, category: uploadSection || 'Floor Plan', description: resolvedFloor }),
        }
      );
      if (!pinRes.ok) throw new Error('Pinpoint convert failed');
      const { pinpointDocument } = await pinRes.json();

      toast.success('Floor plan uploaded and ready');
      setFloorPlans(prev => [...prev, pinpointDocument]);
    } catch (err) {
      console.error('Upload error:', err);
      toast.error('Failed to upload floor plan');
    } finally {
      setUploading(false);
      setPendingFile(null);
      setUploadModalStep(1);
      setShowNameModal(false);
    }
  };

  // ─────────────────────────────────────────────────────────
  // Render: Step 2 — pick floor plan
  // ─────────────────────────────────────────────────────────
  if (step === STEP_FLOOR_PLAN) {
    return (
      <div className="at-root">
        <div className="at-step-header">
          <button className="at-back-btn" onClick={() => setStep(STEP_PROPERTY)}>
            <FaArrowLeft />
          </button>
          <h2>Select Floor Plan</h2>
        </div>
        <div className="at-body">
          <input
            ref={uploadInputRef}
            type="file"
            accept="application/pdf,image/*"
            style={{ display: 'none' }}
            onChange={handleFileSelected}
          />
          {loadingPlans ? (
            <div className="at-empty"><p>Loading floor plans…</p></div>
          ) : floorPlans.length === 0 ? (
            <div className="at-empty">
              <FaMapMarkerAlt />
              <p>No floor plans found for {selectedProperty?.name}.</p>
              <button
                className="at-upload-btn"
                disabled={uploading}
                onClick={() => uploadInputRef.current?.click()}
              >
                <FaUpload /> {uploading ? 'Uploading…' : 'Upload Floor Plan'}
              </button>
            </div>
          ) : (
            <div className="at-list">
              {floorPlans.map((plan, i) => {
                const label = plan.name || plan.original_filename || `Floor Plan ${i + 1}`;
                return (
                  <button
                    key={plan.document_id || plan.id || i}
                    className="at-list-item"
                    onClick={async () => {
                      setSelectedPlan(plan);
                      setPins([]);
                      setStep(STEP_MAP);
                      setTimeout(() => renderPdf(plan.file_path), 100);
                      // Load existing pins for this document (plan.id = pinpoint_document id)
                      const docId = plan.id;
                      try {
                        const r = await fetch(getApiEndpoint(`/api/contractor/pins/${docId}`), { headers });
                        if (r.ok) {
                          const { pins: existing } = await r.json();
                          setPins(existing.map((p, i) => ({
                            x_pct: p.x_percent,
                            y_pct: p.y_percent,
                            num: i + 1,
                            saved: true,
                            assetCode: p.label,
                            asset_id: p.asset_id,
                            photo_url: p.photo_url,
                          })));
                        }
                      } catch (_) {}
                    }}
                  >
                    <div>
                      <span>{label}</span>
                    </div>
                    <FaChevronRight className="at-chevron" />
                  </button>
                );
              })}
              <button
                className="at-upload-btn at-upload-btn--list"
                disabled={uploading}
                onClick={() => uploadInputRef.current?.click()}
              >
                <FaUpload /> {uploading ? 'Uploading…' : 'Upload another floor plan'}
              </button>
            </div>
          )}
        </div>

        {/* Upload modal — top-aligned, 3 steps */}
        {showNameModal && (
          <div className="at-upload-modal-overlay" onClick={() => { setShowNameModal(false); setPendingFile(null); setUploadModalStep(1); }}>
            <div className="at-upload-modal" onClick={e => e.stopPropagation()}>
              {/* Step indicators */}
              <div className="at-upload-modal-steps">
                {['Name', 'Floor', 'Section'].map((s, i) => (
                  <div key={s} className={`at-upload-step-dot ${uploadModalStep === i + 1 ? 'active' : uploadModalStep > i + 1 ? 'done' : ''}`}>
                    <span>{i + 1}</span>
                    <label>{s}</label>
                  </div>
                ))}
              </div>

              {uploadModalStep === 1 && (
                <>
                  <h3 className="at-upload-modal-title">Name this floor plan</h3>
                  <p className="at-upload-modal-sub">File: {pendingFile?.name}</p>
                  <div className="at-form-group">
                    <label>Floor plan name</label>
                    <input className="at-input" type="text" value={uploadName} autoFocus
                      onChange={e => setUploadName(e.target.value)}
                      placeholder="e.g. Roof Level Plan" />
                  </div>
                  <div className="at-upload-modal-actions">
                    <button className="at-btn-secondary" onClick={() => { setShowNameModal(false); setPendingFile(null); setUploadModalStep(1); }}>Cancel</button>
                    <button className="at-btn-primary" disabled={!uploadName.trim()} onClick={() => setUploadModalStep(2)}>Next →</button>
                  </div>
                </>
              )}

              {uploadModalStep === 2 && (
                <>
                  <h3 className="at-upload-modal-title">Which floor?</h3>
                  <p className="at-upload-modal-sub">Select or type the floor this plan covers.</p>
                  <div className="at-form-group">
                    <label>Floor</label>
                    {propertyFloors.length > 0 ? (
                      <select className="at-input" value={uploadFloor} onChange={e => { setUploadFloor(e.target.value); setUploadFloorCustom(''); }}>
                        <option value="">Select a floor…</option>
                        {propertyFloors.map(f => (
                          <option key={f.id} value={f.floor_name}>{f.floor_name}</option>
                        ))}
                        <option value="__custom__">+ Add new floor…</option>
                      </select>
                    ) : null}
                    {(uploadFloor === '__custom__' || propertyFloors.length === 0) && (
                      <input className="at-input" style={{ marginTop: propertyFloors.length > 0 ? 8 : 0 }}
                        type="text" value={uploadFloorCustom} placeholder="e.g. Roof Level, Basement…"
                        onChange={e => setUploadFloorCustom(e.target.value)} autoFocus />
                    )}
                  </div>
                  <div className="at-upload-modal-actions">
                    <button className="at-btn-secondary" onClick={() => setUploadModalStep(1)}>← Back</button>
                    <button className="at-btn-primary" onClick={() => setUploadModalStep(3)}>Next →</button>
                  </div>
                </>
              )}

              {uploadModalStep === 3 && (
                <>
                  <h3 className="at-upload-modal-title">Tag a section</h3>
                  <p className="at-upload-modal-sub">What area does this plan cover? (optional)</p>
                  <div className="at-form-group">
                    <label>Section / area</label>
                    <select className="at-input" value={uploadSection} onChange={e => setUploadSection(e.target.value)}>
                      <option value="">General / whole floor</option>
                      {['Roof Safety','Fire Safety','Electrical','HVAC','Structural','North Wing','South Wing','East Wing','West Wing'].map(s => (
                        <option key={s} value={s}>{s}</option>
                      ))}
                    </select>
                  </div>
                  <div className="at-upload-modal-actions">
                    <button className="at-btn-secondary" onClick={() => setUploadModalStep(2)}>← Back</button>
                    <button className="at-btn-primary" disabled={uploading} onClick={handleConfirmUpload}>
                      <FaUpload /> {uploading ? 'Uploading…' : 'Upload'}
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    );
  }

  // ─────────────────────────────────────────────────────────
  // Render: Step 3 — map + bottom sheet
  // ─────────────────────────────────────────────────────────
  const allPins = [
    ...pins,
    ...(pendingPin && !sheetOpen ? [] : []),
  ];

  return (
    <div className="at-root">
      {/* Success flash */}
      {successFlash && (
        <div className="at-success-flash">
          <FaCheck /> Asset saved successfully
        </div>
      )}

      <div className="at-step-header">
        <button className="at-back-btn" onClick={() => {
          setStep(STEP_FLOOR_PLAN);
          setSheetOpen(false);
          setPendingPin(null);
        }}>
          <FaArrowLeft />
        </button>
        <h2>{selectedProperty?.name}</h2>
      </div>

      <div className="at-body at-canvas-stage">
        <p className="at-canvas-hint">
          <FaMapMarkerAlt /> Tap anywhere on the floor plan to tag an asset
        </p>

        {/* PDF canvas */}
        <div ref={outerRef} className="at-canvas-outer">
          <div
            className="at-canvas-wrap"
            ref={wrapRef}
            onMouseDown={(e) => {
              lastTouchRef.current = { x: e.clientX, y: e.clientY, dragged: false };
            }}
            onMouseMove={(e) => {
              if (!lastTouchRef.current || isPinchingRef.current) return;
              const dx = e.clientX - lastTouchRef.current.x;
              const dy = e.clientY - lastTouchRef.current.y;
              if (!lastTouchRef.current.dragged && Math.hypot(dx, dy) > 4) {
                lastTouchRef.current.dragged = true;
              }
              if (lastTouchRef.current.dragged) {
                setPan(p => ({ x: p.x + dx, y: p.y + dy }));
                lastTouchRef.current = { ...lastTouchRef.current, x: e.clientX, y: e.clientY };
              }
            }}
            onMouseUp={(e) => {
              const wasDrag = lastTouchRef.current?.dragged;
              lastTouchRef.current = null;
              if (!wasDrag) handleCanvasTap(e);
            }}
            onMouseLeave={() => { lastTouchRef.current = null; }}
            onTouchStart={(e) => {
              if (e.touches.length === 2) {
                isPinchingRef.current = true;
                const dx = e.touches[0].clientX - e.touches[1].clientX;
                const dy = e.touches[0].clientY - e.touches[1].clientY;
                lastTouchRef.current = { dist: Math.hypot(dx, dy), zoom };
              } else if (e.touches.length === 1) {
                lastTouchRef.current = { x: e.touches[0].clientX, y: e.touches[0].clientY, dragged: false };
              }
            }}
            onTouchMove={(e) => {
              if (e.touches.length === 2 && lastTouchRef.current) {
                e.preventDefault();
                const dx = e.touches[0].clientX - e.touches[1].clientX;
                const dy = e.touches[0].clientY - e.touches[1].clientY;
                const dist = Math.hypot(dx, dy);
                const newZoom = Math.min(5, Math.max(1, lastTouchRef.current.zoom * (dist / lastTouchRef.current.dist)));
                setZoom(newZoom);
                if (newZoom === 1) setPan({ x: 0, y: 0 });
              } else if (e.touches.length === 1 && lastTouchRef.current) {
                e.preventDefault();
                const t = e.touches[0];
                const dx = t.clientX - lastTouchRef.current.x;
                const dy = t.clientY - lastTouchRef.current.y;
                if (!lastTouchRef.current.dragged && Math.hypot(dx, dy) > 6) {
                  lastTouchRef.current.dragged = true;
                }
                if (lastTouchRef.current.dragged) {
                  setPan(p => ({ x: p.x + dx, y: p.y + dy }));
                }
                lastTouchRef.current = { ...lastTouchRef.current, x: t.clientX, y: t.clientY };
              }
            }}
            onTouchEnd={(e) => {
              if (e.touches.length < 2) {
                const wasDrag = lastTouchRef.current?.dragged;
                setTimeout(() => { isPinchingRef.current = false; }, 100);
                lastTouchRef.current = null;
                if (!wasDrag && !isPinchingRef.current) handleCanvasTap(e);
              }
            }}
            style={{
              transform: `scale(${zoom}) translate(${pan.x / zoom}px, ${pan.y / zoom}px)`,
              transformOrigin: '0 0',
              willChange: 'transform',
              cursor: 'grab',
            }}
          >
          <div style={{ position: 'relative', display: 'inline-block', width: '100%' }}>
            <canvas ref={canvasRef} style={{ display: 'block', width: '100%', height: 'auto' }} />
            {pdfLoading && (
              <div className="at-canvas-loading">Loading floor plan…</div>
            )}
            {/* Pins overlay — inset:0 matches the canvas exactly */}
            <div className="at-pins-overlay">
              {pins.map((pin, i) => (
                <div
                  key={i}
                  className={`at-pin ${pin.saved ? 'saved' : 'pending'}`}
                  style={{ left: `${pin.x_pct}%`, top: `${pin.y_pct}%` }}
                  title={pin.assetCode || ''}
                  onClick={e => { e.stopPropagation(); if (pin.saved) setSelectedPin(pin); }}
                >
                  {pin.num}
                </div>
              ))}
              {pendingPin && (
                <div
                  className="at-pin pending"
                  style={{ left: `${pendingPin.x_pct}%`, top: `${pendingPin.y_pct}%` }}
                >
                  ?
                </div>
              )}
            </div>
          </div>
          </div>
        </div>

        {/* Existing pin popup */}
        {selectedPin && (
          <div className="at-pin-popup" onClick={() => setSelectedPin(null)}>
            <div className="at-pin-popup-card" onClick={e => e.stopPropagation()}>
              <div className="at-pin-popup-code">{selectedPin.assetCode}</div>
              {selectedPin.photo_url && (
                <img
                  className="at-pin-popup-photo"
                  src={`${selectedPin.photo_url.startsWith('http') ? '' : '/api/'}${selectedPin.photo_url}`}
                  alt="Asset"
                />
              )}
              <div className="at-pin-popup-actions">
                <button
                  className="at-pin-popup-btn"
                  onClick={() => {
                    if (navigator.clipboard) {
                      navigator.clipboard.writeText(selectedPin.assetCode).catch(() => {});
                    } else {
                      const el = document.createElement('textarea');
                      el.value = selectedPin.assetCode;
                      document.body.appendChild(el); el.select();
                      document.execCommand('copy');
                      document.body.removeChild(el);
                    }
                  }}
                >
                  <FaCopy /> Copy ID
                </button>
                <button className="at-pin-popup-close" onClick={() => setSelectedPin(null)}>Close</button>
              </div>
            </div>
          </div>
        )}

        {/* Session list */}
        {sessionAssets.length > 0 && (
          <div className="at-session-list">
            <div className="at-session-list-header">
              Tagged this session ({sessionAssets.length})
            </div>
            {sessionAssets.map((a, i) => (
              <div key={i} className="at-session-row">
                <span className="at-session-code">{a.assetCode}</span>
                <span className="at-session-type">{a.typeLabel}</span>
                <button
                  className={`at-session-copy ${copiedCode === a.assetCode ? 'copied' : ''}`}
                  onClick={() => copyCode(a.assetCode)}
                >
                  {copiedCode === a.assetCode ? <><FaCheck /> Copied</> : <><FaCopy /> Copy</>}
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Bottom sheet */}
      {sheetOpen && (
        <div className="at-sheet-overlay" onClick={() => { setSheetOpen(false); setPendingPin(null); }}>
          <div className="at-sheet" onClick={e => e.stopPropagation()}>
            <div className="at-sheet-handle" />
            <button className="at-sheet-close" onClick={() => { setSheetOpen(false); setPendingPin(null); }}>
              <FaTimes />
            </button>
            <div className="at-sheet-inner">
              {savedAsset ? (
                /* ── Post-save confirmation ── */
                <div className="at-saved-screen">
                  <div className="at-saved-icon">✓</div>
                  <p className="at-saved-label">Asset Saved</p>
                  <p className="at-saved-type">{savedAsset.catLabel}</p>
                  <div className="at-code-display at-code-display--large">
                    <span className="at-code-value">{savedAsset.code}</span>
                    <button
                      className={`at-code-copy-btn ${copiedCode === savedAsset.code ? 'copied' : ''}`}
                      onClick={() => {
                        navigator.clipboard?.writeText(savedAsset.code).catch(() => {
                          const el = document.createElement('textarea');
                          el.value = savedAsset.code;
                          document.body.appendChild(el);
                          el.select();
                          document.execCommand('copy');
                          document.body.removeChild(el);
                        });
                        setCopiedCode(savedAsset.code);
                        setTimeout(() => setCopiedCode(null), 2000);
                      }}
                    >
                      {copiedCode === savedAsset.code ? <><FaCheck /> Copied!</> : <><FaCopy /> Copy ID</>}
                    </button>
                  </div>
                  <div className="at-sheet-actions">
                    <button
                      className="at-btn-hydra"
                      onClick={() => {
                        const params = new URLSearchParams({ reference: savedAsset.code, source: 'cloudsafe' });
                        const deepLink = `hydrajaws://tag?${params.toString()}`;
                        const fallback = `https://app.hydrajaws.com/tag?${params.toString()}`;
                        const appStore = /iphone|ipad|ipod/i.test(navigator.userAgent)
                          ? 'https://apps.apple.com/app/hydrajaws/id6444215635'
                          : 'https://play.google.com/store/apps/details?id=com.hydrajaws.app';
                        const startedAt = Date.now();
                        window.location.href = deepLink;
                        setTimeout(() => {
                          if (Date.now() - startedAt < 2500 && document.visibilityState === 'visible') {
                            window.open(appStore, '_blank', 'noopener,noreferrer');
                          }
                        }, 1500);
                      }}
                    >
                      <FaExternalLinkAlt /> Open in HydraJaws
                    </button>
                    <button
                      className="at-btn-save"
                      onClick={() => { setSavedAsset(null); setPendingPin(null); setSheetOpen(false); }}
                    >
                      Done
                    </button>
                  </div>
                </div>
              ) : (<>

              <p className="at-sheet-title">Tag New Asset</p>

              {/* Asset code preview */}
              {/* Category */}
              <div className="at-field-group">
                <label className="at-field-label">Category</label>
                <select className="at-select" value={catKey} onChange={e => {
                  const k = e.target.value;
                  const firstSub = CATALOG[k]?.subtypes?.[0];
                  setCatKey(k);
                  setSubtypeVal(firstSub?.value || '');
                  setVariantVal(firstSub?.variants?.[0] || '');
                  setFixingVal(firstSub?.fixings?.[0] || '');
                }}>
                  {CATALOG_KEYS.map(k => <option key={k} value={k}>{CATALOG[k].label}</option>)}
                </select>
              </div>

              {/* Type */}
              {catDef?.subtypes?.length > 0 && (
                <div className="at-field-group">
                  <label className="at-field-label">Type</label>
                  <select className="at-select" value={subtypeVal} onChange={e => {
                    const sub = catDef.subtypes.find(s => s.value === e.target.value);
                    setSubtypeVal(e.target.value);
                    setVariantVal(sub?.variants?.[0] || '');
                    setFixingVal(sub?.fixings?.[0] || '');
                  }}>
                    {catDef.subtypes.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                  </select>
                </div>
              )}

              {/* Variant (Material) */}
              {variantOptions.length > 0 && (
                <div className="at-field-group">
                  <label className="at-field-label">Material / Variant</label>
                  <select className="at-select" value={variantVal} onChange={e => setVariantVal(e.target.value)}>
                    {variantOptions.map(v => <option key={v.value} value={v.value}>{v.label}</option>)}
                  </select>
                </div>
              )}

              {/* Fixing Method */}
              {fixingOptions.length > 0 && (
                <div className="at-field-group">
                  <label className="at-field-label">Fixing Method</label>
                  <select className="at-select" value={fixingVal} onChange={e => setFixingVal(e.target.value)}>
                    {fixingOptions.map(f => <option key={f.value} value={f.value}>{f.label}</option>)}
                  </select>
                </div>
              )}

              {/* Location description */}
              <div className="at-field-group">
                <label className="at-field-label">Location Description</label>
                <input className="at-input" placeholder="e.g. North-east corner, roof level 3" value={locationDesc} onChange={e => setLocationDesc(e.target.value)} />
              </div>

              {/* Notes */}
              <div className="at-field-group">
                <label className="at-field-label">Notes <span className="at-optional">(optional)</span></label>
                <textarea className="at-textarea" rows={2} placeholder="Any additional notes…" value={notes} onChange={e => setNotes(e.target.value)} />
              </div>

              {/* Photo capture */}
              <div className="at-field-group">
                <label className="at-field-label">Photo <span className="at-optional">(optional)</span></label>
                <input
                  ref={photoInputRef}
                  type="file"
                  accept="image/*"
                  capture="environment"
                  style={{ display: 'none' }}
                  onChange={e => {
                    const f = e.target.files?.[0];
                    if (!f) return;
                    setPhotoFile(f);
                    setPhotoPreview(URL.createObjectURL(f));
                  }}
                />
                {photoPreview ? (
                  <div className="at-photo-preview">
                    <img src={photoPreview} alt="Asset" className="at-photo-thumb" />
                    <button type="button" className="at-photo-remove" onClick={() => { setPhotoFile(null); setPhotoPreview(null); }}>
                      <FaTimes /> Remove
                    </button>
                  </div>
                ) : (
                  <button type="button" className="at-photo-btn" onClick={() => photoInputRef.current?.click()}>
                    📷 Take Photo
                  </button>
                )}
              </div>

              {/* Actions */}
              <div className="at-sheet-actions">
                <button
                  className="at-btn-save"
                  onClick={handleSave}
                  disabled={saving || !locationDesc.trim()}
                >
                  {saving ? <span className="at-spinner" /> : <FaSave />}
                  {saving ? 'Saving…' : 'Save Asset'}
                </button>

                <button
                  className="at-btn-cancel"
                  onClick={() => { setSheetOpen(false); setPendingPin(null); }}
                >
                  <FaTimes /> Cancel
                </button>
              </div>
              </>)}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
